import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import { User } from '../types';
import { Building2, ChevronDown, Bell } from 'lucide-react';
import { isBusinessOwner, getUserPrimaryRole } from '../utils/roleUtils';
import BusinessOwnerAskAiWidget from './BusinessOwnerAskAiWidget';

interface NavigationBarProps {
  unreadNotificationsCount?: number;
  onNotificationsClick?: () => void;
}

const NavigationBar: React.FC<NavigationBarProps> = ({
  unreadNotificationsCount = 0,
  onNotificationsClick
}) => {
  const { user, logout } = useAuth();
  const { currentBusiness, userBusinesses, setCurrentBusiness, isLoading } = useBusiness();
  const navigate = useNavigate();
  
  // Check if user is a customer (has no business access)
  const isCustomer = user && userBusinesses.length === 0;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isBusinessDropdownOpen, setIsBusinessDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const businessDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (businessDropdownRef.current && !businessDropdownRef.current.contains(event.target as Node)) {
        setIsBusinessDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      // Get stored login type before logout (stored in localStorage)
      const storedLoginType = localStorage.getItem('loginType');
      
      await logout();
      
      // Clear stored login type
      localStorage.removeItem('loginType');
      
      // Redirect based on stored login type
      if (storedLoginType === 'professional') {
        navigate('/login?type=professional');
      } else if (storedLoginType === 'business') {
        navigate('/login?type=business');
      } else {
        // Default to business login for NavigationBar (used in business dashboard)
        navigate('/login?type=business');
      }
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const getUserInitials = (user: User) => {
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getUserDisplayName = (user: User) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.email;
  };

  if (!user) return null;

  const currentBusinessRole = !isCustomer && currentBusiness?.id
    ? getUserPrimaryRole(user, currentBusiness.id)
    : null;

  return (
    <>
    <nav className="bg-white shadow-sm border-b border-gray-200">
      <div className="flex justify-between items-center h-16 px-0">
        {/* Logo/Brand */}
        <div className="flex items-center space-x-4">
          {isCustomer ? (
            <div className="flex flex-col pl-4">
              <Link
                to="/"
                className="text-xl font-semibold text-gray-900 hover:text-[#0B4778] transition-colors duration-200 cursor-pointer"
              >
                Appointment Booking <span className="text-[#0B4778] font-bold">Platform</span>
              </Link>
              <p className="text-xs text-gray-500">
                powered by AACORE
              </p>
            </div>
          ) : (
            <div className="flex items-center space-x-4 pl-4">
              {/* AACore Link */}
              <Link
                to="/"
                className="hidden sm:inline text-[#0B4778] hover:text-[#0a3d66] font-semibold text-lg transition-colors duration-200"
              >
                AACore
              </Link>
              <span className="hidden sm:inline text-gray-400 text-lg">|</span>
              
              {/* Business Name */}
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
                {isLoading ? 'Loading...' : currentBusiness?.name || 'Appointment System'}
              </h1>
              
              {/* Business Switcher */}
              {userBusinesses.length > 1 && (
                <div className="relative" ref={businessDropdownRef}>
                  <button
                    onClick={() => setIsBusinessDropdownOpen(!isBusinessDropdownOpen)}
                    className="flex items-center space-x-2 px-3 py-1 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#0B4778]"
                  >
                    <Building2 className="w-4 h-4" />
                    <span>Switch Business</span>
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${
                      isBusinessDropdownOpen ? 'rotate-180' : ''
                    }`} />
                  </button>

                  {/* Business Dropdown Menu */}
                  {isBusinessDropdownOpen && (
                    <div className="absolute left-0 mt-2 w-64 bg-white rounded-md shadow-lg py-1 z-[60] border border-gray-200">
                      <div className="px-3 py-2 border-b border-gray-100">
                        <p className="text-xs font-medium text-gray-500">Your Businesses</p>
                      </div>
                      {userBusinesses.map((business) => (
                        <button
                          key={business.id}
                          onClick={() => {
                            setCurrentBusiness(business);
                            setIsBusinessDropdownOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-sm transition-colors duration-150 ${
                            currentBusiness?.id === business.id
                              ? 'bg-[#0B4778]/10 text-[#0B4778] font-medium'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <div className="font-medium">{business.name}</div>
                          <div className="text-xs text-gray-500">{business.shortName}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right side: Notifications and User Profile */}
        <div className="flex items-center space-x-2 pr-4">
          {/* Notification Bell */}
          {onNotificationsClick && (
            <button
              onClick={onNotificationsClick}
              className="relative text-gray-700 hover:text-gray-900 transition-colors duration-200 p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#0B4778]"
              aria-label="Notifications"
            >
              <Bell className="w-5 h-5" />
              {unreadNotificationsCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadNotificationsCount > 9 ? '9+' : unreadNotificationsCount}
                </span>
              )}
            </button>
          )}

          {/* User Profile Dropdown */}
          <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#0B4778]"
          >
            {/* User Avatar */}
            <div className="w-8 h-8 bg-[#0B4778] rounded-full flex items-center justify-center text-white text-sm font-medium">
              {getUserInitials(user)}
            </div>
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-auto min-w-[12rem] max-w-[20rem] bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
              {/* User Info */}
              <div className="px-4 py-2 border-b border-gray-100">
                <p className="text-sm font-medium text-gray-900 whitespace-nowrap">{getUserDisplayName(user)}</p>
                {currentBusinessRole && (
                  <p className="text-xs text-gray-500 whitespace-nowrap mt-0.5">{currentBusinessRole}</p>
                )}
              </div>

              {/* Menu Items */}
              <div className="py-1">
                <button
                  onClick={() => {
                    navigate('/dashboard/customer');
                    setIsDropdownOpen(false);
                  }}
                  className="w-full text-left flex items-start gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                >
                  <i className="bi bi-grid text-[#0B4778] mt-0.5 flex-shrink-0"></i>
                  <span className="flex flex-col items-start text-left">
                    <span className="leading-tight whitespace-nowrap">My Dashboard</span>
                    <span className="text-xs text-gray-500 leading-tight whitespace-nowrap">As a customer</span>
                  </span>
                </button>

                <div className="my-1 border-t border-gray-100"></div>

                <button
                  onClick={() => {
                    navigate('/dashboard/business/profile');
                    setIsDropdownOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                >
                  <i className="bi bi-person-gear text-[#0B4778]"></i>
                  Profile Settings
                </button>
                
                {/* Only show Account Settings for Business Owners */}
                {/* Temporarily disabled */}
                {/* {isBusinessOwner(user) && (
                  <Link
                    to="/account-settings"
                    onClick={() => setIsDropdownOpen(false)}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                  >
                    <i className="bi bi-briefcase text-teal-600"></i>
                    Business Account Settings
                  </Link>
                )} */}

                <button
                  onClick={() => {
                    navigate('/help-support');
                    setIsDropdownOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                >
                  <i className="bi bi-question-circle text-amber-500"></i>
                  Help & Support
                </button>
              </div>

              {/* Logout Button */}
              <div className="border-t border-gray-100 pt-1">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-150"
                >
                  <i className="bi bi-box-arrow-right"></i>
                  Sign Out
                </button>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    </nav>
    <BusinessOwnerAskAiWidget />
    </>
  );
};

export default NavigationBar; 